import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import FloatingBackToTop from '@/components/feature/FloatingBackToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import ScrollToTop from '@/components/feature/ScrollToTop';

const milestones = [
  { year: '2018', key: 'milestone2018' },
  { year: '2019', key: 'milestone2019' },
  { year: '2020', key: 'milestone2020' },
  { year: '2021', key: 'milestone2021' },
  { year: '2022', key: 'milestone2022' },
  { year: '2023', key: 'milestone2023' },
  { year: '2024', key: 'milestone2024' },
];

const reviews = [
  { name: '张女士', rating: 5, text: '在岛国精品买了好几次了，每次都是正品，包装也很用心。特别是护肤品，日期新鲜，跟我在日本买的一模一样。物流也很快，三天就到了！' },
  { name: '李先生', rating: 5, text: '作为一个日本零食爱好者，岛国精品简直是我的天堂。白色恋人和ROYCE巧克力都是正品，价格也很合理。客服态度特别好，有问题马上解决。' },
  { name: '王小姐', rating: 5, text: '朋友推荐来的，果然没让我失望。东西都是日本原装的，品质没话说。已经推荐给身边所有喜欢日本商品的朋友了！' },
  { name: '陈先生', rating: 4, text: '买了一个象印保温杯，使用感很棒，保温效果确实好。发货速度也快，包装很仔细。唯一小小建议是希望能有更多数码产品选择。' },
  { name: '刘女士', rating: 5, text: '自从发现了岛国精品，家里日用品基本都换成日本的了。MUJI的香薰机、贝亲的奶瓶，每一样都好用。正品保障让人买得放心。' },
  { name: '赵先生', rating: 5, text: '已经是老顾客了，在岛国精品买了一年了。品质稳定，每次都是正品。积分兑换也很划算，会员专属价格经常有惊喜。强烈推荐！' },
];

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formErrorMsg, setFormErrorMsg] = useState('');

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('submitting');
    setFormErrorMsg('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qa6a6e3oq4jqumtj60', {
        method: 'POST',
        body: new URLSearchParams(formData as unknown as Record<string, string>),
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      const responseText = await response.text();
      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setFormErrorMsg(responseText || t('formError'));
      }
    } catch {
      setFormStatus('error');
      setFormErrorMsg(t('formError'));
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 岛国精品｜日本原装进口商品精选平台"
        description="了解岛国精品的品牌故事、发展历程、品质承诺和联系方式。我们致力于为中国消费者带来最纯正的日本商品体验。"
        keywords="岛国精品品牌,日本进口商品平台,关于岛国精品,进口商品品牌介绍"
        canonicalUrl="https://www.daoguojingpin.com/about"
      />

      <SchemaOrg type="Organization" data={{
        name: '岛国精品（上海）国际贸易有限公司',
        url: 'https://www.daoguojingpin.com',
        description: '岛国精品是一家专注于日本原装进口商品的精选平台，成立于2018年，总部位于上海。',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '陆家嘴金融贸易区世纪大道100号',
          addressLocality: '上海',
          addressRegion: '浦东新区',
          addressCountry: 'CN',
        },
        telephone: '+86-400-888-9999',
        email: 'service@daoguojingpin.com',
      }} />

      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.daoguojingpin.com' },
          { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.daoguojingpin.com/about' },
        ],
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16 md:pt-20">
          <Breadcrumb items={[{ label: t('pageTitle') }]} />

          <section className="w-full px-4 md:px-6 py-10 md:py-16">
            <div className="max-w-7xl mx-auto">
              <div className="text-center mb-12">
                <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-3">
                  {t('pageTitle')}
                </h1>
                <p className="text-foreground-500 text-sm md:text-base max-w-2xl mx-auto">
                  {t('pageDescription')}
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
                <div>
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20Japanese%20style%20office%20interior%20with%20warm%20wooden%20furniture%2C%20minimalist%20design%2C%20products%20displayed%20on%20shelves%2C%20natural%20lighting%20from%20large%20windows%2C%20warm%20cream%20walls%2C%20professional%20yet%20welcoming%20atmosphere%2C%20high%20quality%20editorial%20photography&width=800&height=600&seq=about-brand-01&orientation=landscape"
                    alt="岛国精品品牌"
                    className="w-full h-72 md:h-96 object-cover rounded-lg"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col justify-center">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-4">
                    {t('brandIntro')}
                  </h2>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                    {t('brandIntroContent')}
                  </p>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0 mt-0.5">
                        <i className="ri-focus-3-line text-sm"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{t('mission')}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{t('missionContent')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0 mt-0.5">
                        <i className="ri-eye-line text-sm"></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-1">{t('vision')}</h3>
                        <p className="text-xs text-foreground-500 leading-relaxed">{t('visionContent')}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-background-100">
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-10 text-center">
                {t('milestones')}
              </h2>
              <div className="relative">
                <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
                <div className="space-y-8">
                  {milestones.map((m, index) => (
                    <div key={m.year} className={`relative flex items-start gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                      <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-accent-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                      <div className={`pl-10 md:pl-0 md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <span className="font-display text-2xl font-bold text-accent-600">{m.year}</span>
                        <p className="text-sm text-foreground-600 mt-1 leading-relaxed">{t(m.key)}</p>
                      </div>
                      <div className="hidden md:block md:w-1/2"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-background-50">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {t('reputation')}
                </h2>
                <p className="text-foreground-500 text-sm">{t('reputationDesc')}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {reviews.map((review, index) => (
                  <div key={index} className="bg-background-100 rounded-lg p-5">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <i key={i} className={`ri-star-fill text-sm ${i < review.rating ? 'text-accent-500' : 'text-background-300'}`}></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{review.text}&rdquo;
                    </p>
                    <p className="text-xs font-semibold text-foreground-900">{review.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="font-heading text-xl md:text-3xl font-bold text-foreground-900 mb-3">
                  {t('contactTitle')}
                </h2>
                <p className="text-foreground-500 text-sm">{t('contactDesc')}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <div className="space-y-5">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className="ri-building-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-1">{t('companyName')}</p>
                        <p className="text-xs text-foreground-500">{t('address')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-phone-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-1">{t('phoneLabel')}</p>
                        <p className="text-xs text-foreground-500">{t('phone')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-1">{t('emailLabel')}</p>
                        <p className="text-xs text-foreground-500">{t('email')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 flex-shrink-0">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-1">{t('wechatLabel')}</p>
                        <p className="text-xs text-foreground-500">{t('wechat')}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700 flex-shrink-0">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900 mb-1">{t('workingHours')}</p>
                        <p className="text-xs text-foreground-500">{t('workingHours')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form onSubmit={handleFormSubmit} data-readdy-form className="bg-background-50 rounded-lg p-6 space-y-4">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">{t('formName')}</label>
                      <input
                        type="text"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">{t('formEmail')}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">{t('formPhone')}</label>
                      <input
                        type="tel"
                        name="phone"
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200"
                        placeholder="请输入您的电话（选填）"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-foreground-700 mb-1.5">{t('formMessage')}</label>
                      <textarea
                        name="message"
                        required
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-400 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言内容（500字以内）"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'submitting'}
                      className="w-full py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-all duration-300 disabled:opacity-60 whitespace-nowrap"
                    >
                      {formStatus === 'submitting' ? t('loading') : t('formSubmit')}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-xs text-accent-700 bg-accent-100/50 text-center py-2 rounded-lg">
                        {t('formSuccess')}
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-xs text-primary-700 bg-primary-100/50 text-center py-2 rounded-lg">
                        {formErrorMsg}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <FloatingBackToTop />
      </div>
    </>
  );
}